import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  createPass,
  listPasses,
  passAction,
  updatePass,
} from '../../../../api/visitor-passes-client';
import type {
  PassListResponse,
  PassState,
  VisitorPass,
} from '../../../../interfaces/visitor-pass';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { PassEditorModal, readPassError } from '../../../pass-editor/pass-editor';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { LoadError, ListSkeleton } from '../dashboard-shared-components/load-state';
import { Staleable, StateSwitch, StatusLabel, listItemMotion, m } from '../../../../motion';
import toast from '../../../../utils/toast';
import {
  describeSchedule,
  describeVisit,
  formatPassDate,
  zoneNote,
} from '../../../../utils/pass-schedule';
import { zoneLabel } from '../../../../function-utils/building-time';
import { down } from '../../../../style-utils/media-queries';
import { VisuallyHidden } from '../../../../style-utils/text';
import { palette, radius, space, spacing, typography } from '../../../../style-utils/theme';
import { occupantFullName } from './units-shared';

/**
 * EXPECTED VISITORS ON THE UNIT SCREEN (resident-portal RP-2.10).
 *
 * Only about one resident in twelve has a login, so a pass a property manager
 * adds on a resident's behalf is how most units get one. Each resident living
 * in the unit now gets their LIVE passes listed under their row, and — for
 * whoever holds visitorpass:manage — "Add expected visitor", Edit, Pause /
 * Resume and End.
 *
 * ONE READ PER UNIT. `useUnitPasses` asks GET /buildings/:b/passes?state=live&
 * unit_id=… once; each resident's block filters it to their own occupancy. The
 * same answer carries `today` and `zone` (the building's calendar date and time
 * zone — every date and time here is the building's) and `limits`, which the
 * editor's inputs take their bounds from.
 *
 * LIVE is the server's definition: not ended, not past its end date, and the
 * host still living here. So a pass disappears from this list the moment it is
 * ended, and a moved-out resident's passes are not shown at all.
 */

/* ─────────────────────────────  The read  ─────────────────────────────── */

export type UnitPassesStatus = 'idle' | 'loading' | 'error' | 'ready';

export interface UnitPassesState {
  status: UnitPassesStatus;
  data: PassListResponse | null;
  /** A refetch is in flight over data already shown. */
  refreshing: boolean;
  /** A refetch failed; the last good data is still shown. */
  refreshFailed: boolean;
  reload: () => void;
}

export const useUnitPasses = (
  buildingId: number | null | undefined,
  unitId: number | null | undefined,
  enabled: boolean
): UnitPassesState => {
  const [data, setData] = useState<PassListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  // The read-sequence guard: only the LATEST request may write state, so a
  // slow first answer cannot land on top of a quicker reload.
  const seq = useRef(0);
  // Another unit's passes are not this unit's stale data: a new unit starts
  // from a skeleton, a reload of the same one keeps its rows and dims them.
  const loadedFor = useRef('');
  const active = enabled && !!buildingId && !!unitId;

  useEffect(() => {
    if (!active || !buildingId || !unitId) {
      setLoading(false);
      setFailed(false);

      return undefined;
    }

    const key = `${buildingId}:${unitId}`;
    if (loadedFor.current !== key) {
      loadedFor.current = key;
      setData(null);
    }
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setFailed(false);

    listPasses(buildingId, { state: 'live', unitId, limit: 100 })
      .then(response => {
        if (seq.current === mine) setData(response);
      })
      .catch(() => {
        if (seq.current === mine) setFailed(true);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });

    return () => {
      seq.current += 1;
    };
  }, [active, buildingId, unitId, tick]);

  const reload = useCallback(() => setTick(n => n + 1), []);

  const status: UnitPassesStatus = !active
    ? 'idle'
    : data
    ? 'ready'
    : failed
    ? 'error'
    : 'loading';

  return {
    status,
    data: active ? data : null,
    refreshing: active && loading && !!data,
    refreshFailed: active && failed && !!data,
    reload,
  };
};

/* ─────────────────────────────  Wording  ──────────────────────────────── */

const possessive = (name: string): string => `${name}'s`;

const visitorName = (pass: VisitorPass): string =>
  [pass.first_name, pass.last_name].filter(Boolean).join(' ') || 'Visitor';

const firstNameOf = (pass: VisitorPass): string => pass.first_name || visitorName(pass);

const TONES: Record<PassState, StatusTone> = {
  active: 'success',
  paused: 'warning',
  scheduled: 'warning',
  ended: 'neutral',
  pending: 'warning',
};

const toneOf = (pass: VisitorPass): StatusTone => (pass.state ? TONES[pass.state] : 'neutral');

const stateLabel = (pass: VisitorPass): string => {
  switch (pass.state) {
    case 'active':
      return 'Active';
    case 'pending':
      return 'Waiting for approval';
    case 'paused':
      return pass.paused_until
        ? `Paused until ${formatPassDate(pass.paused_until, { day: 'numeric', month: 'short' })}`
        : 'Paused';
    case 'scheduled':
      return `Starts ${formatPassDate(pass.valid_from, { day: 'numeric', month: 'short' })}`;
    default:
      return 'Ended';
  }
};

const nextLine = (pass: VisitorPass, zone: string, today: string): string => {
  if (pass.next) {
    const zoneSuffix = zoneNote(zone) ? ` · ${zoneLabel(zone)}` : '';

    return `Next visit ${describeVisit(pass.next, zone, today)}${zoneSuffix}`;
  }

  if (pass.state === 'pending') return 'No visits until a manager approves it';

  return pass.state === 'paused' ? 'No visits while paused' : 'No more visits on this pass';
};

/* Said instead of the buttons the server would refuse (RP-D13 wording). */
export const PASSES_OFF_NOTE =
  "Visitor passes are switched off at this building, so passes can't be added or changed. Ending one still works.";
export const PASSES_ARCHIVED_NOTE =
  "This building is archived, so passes can't be added or changed. Ending one still works.";

/* ──────────────────────  One resident's expected visitors  ────────────── */

type RowAction = 'pause' | 'resume' | 'revoke';

type EditorTarget = { pass: VisitorPass | null } | null;
type Confirming = { kind: 'pause' | 'end'; pass: VisitorPass } | null;

export interface OccupantPassesProps {
  occupant: { id: string; first_name: string; last_name: string };
  /** The unit's number, named in the editor's subtitle. */
  unitLabel: string;
  buildingId: number;
  passes: UnitPassesState;
  /**
   * The caller holds a pass-write permission (visitorpass:manage, or the
   * desk's visitorpass:desk_write). WHAT they may do is the server's answer:
   * the list's `pass_access` (may add? one-time only?) and each pass's
   * `can_edit` / `can_end` (migration 118's per-building desk setting).
   * This flag is only the fallback when an answer carries neither.
   */
  canManage: boolean;
}

export const OccupantPasses: React.FC<OccupantPassesProps> = ({
  occupant,
  unitLabel,
  buildingId,
  passes,
  canManage,
}) => {
  const headingId = useId();
  const [editor, setEditor] = useState<EditorTarget>(null);
  // Changes on every OPEN, never on close: each open is a fresh editor, and
  // a closing one keeps its key so it can animate out.
  const [editorKey, setEditorKey] = useState(0);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [resuming, setResuming] = useState<string | null>(null);
  const inFlight = useRef(new Set<string>());
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The pass just ended. Its row — and the End button the dialog hands focus
  // back to — leave with the next read, so focus moves to this section's
  // heading instead of falling to the page.
  const endedId = useRef<string | null>(null);

  const { data, reload } = passes;
  const access = data?.pass_access;
  // The building's own switch (the register's "Use visitor passes in this
  // building"), or an archived building. Either way the server refuses every
  // add, edit, pause and resume (409 PASSES_DISABLED) and still allows End, so
  // the screen offers exactly that instead of buttons that fail on save.
  const accepts = !data || (data.passes_enabled !== false && data.building_active !== false);
  const canAdd = accepts && canManage && (access ? access.can_add : true);
  const editorMode = access?.once_only ? 'staff-once' : 'staff';
  const mayEdit = (pass: VisitorPass) => accepts && (pass.can_edit ?? canManage);
  const mayEnd = (pass: VisitorPass) => pass.can_end ?? canManage;
  const hostName = occupantFullName(occupant);
  const hostFirst = occupant.first_name?.trim() || hostName;

  const rows = useMemo(
    () => (data?.rows ?? []).filter(pass => pass.host_occupancy_id === occupant.id),
    [data, occupant.id]
  );

  useEffect(() => {
    if (endedId.current && !rows.some(pass => pass.pass_id === endedId.current)) {
      endedId.current = null;
      headingRef.current?.focus();
    }
  }, [rows]);

  const view =
    passes.status === 'error'
      ? 'error'
      : passes.status === 'ready'
      ? rows.length
        ? 'ready'
        : 'empty'
      : 'loading';

  const openEditor = (pass: VisitorPass | null) => {
    setEditorKey(n => n + 1);
    setEditor({ pass });
  };

  /* Pause, resume and end. One request per pass at a time; the list is read
     again whatever the answer, so a row never shows a state the server does
     not hold. Toasts report the OUTCOME in our words, never the server's. */
  const runAction = async (pass: VisitorPass, action: RowAction): Promise<void> => {
    if (inFlight.current.has(pass.pass_id)) return;
    inFlight.current.add(pass.pass_id);
    const first = firstNameOf(pass);
    const verb = action === 'revoke' ? 'end' : action;
    try {
      await passAction(pass.pass_id, action);
      if (action === 'revoke') endedId.current = pass.pass_id;
      toast.success(
        action === 'pause'
          ? `${possessive(first)} pass is paused.`
          : action === 'resume'
          ? `${possessive(first)} pass is active again.`
          : `${possessive(first)} pass has ended.`
      );
    } catch (error) {
      const fallback = `Could not ${verb} ${possessive(first)} pass. Try again.`;
      const reading = readPassError(error, {
        limits: data?.limits,
        fallback,
        notFound: 'This pass has already ended.',
      });
      toast.error(reading.status === 400 ? fallback : reading.message);
    } finally {
      inFlight.current.delete(pass.pass_id);
      reload();
    }
  };

  const resume = async (pass: VisitorPass) => {
    setResuming(pass.pass_id);
    try {
      await runAction(pass, 'resume');
    } finally {
      setResuming(null);
    }
  };

  const confirmFirst = confirming ? firstNameOf(confirming.pass) : '';

  return (
    <PassSection aria-labelledby={headingId}>
      <PassHead>
        <PassHeading id={headingId} ref={headingRef} tabIndex={-1}>
          Expected visitors
          <VisuallyHidden> for {hostName}</VisuallyHidden>
        </PassHeading>
        {canAdd && data ? (
          <AppButton
            variant="secondary"
            aria-label={`Add expected visitor for ${hostName}`}
            onClick={() => openEditor(null)}
          >
            Add expected visitor
          </AppButton>
        ) : null}
      </PassHead>

      {!accepts ? (
        <PassEmpty role="note">
          {data?.building_active === false
            ? PASSES_ARCHIVED_NOTE
            : PASSES_OFF_NOTE}
        </PassEmpty>
      ) : null}

      {passes.refreshFailed ? (
        <LoadError
          message="Could not refresh the expected visitors for this unit."
          onRetry={reload}
        />
      ) : null}

      <StateSwitch state={view}>
        {view === 'loading' ? (
          <ListSkeleton rows={1} rowHeight={56} label="expected visitors" />
        ) : view === 'error' ? (
          <LoadError
            message="Could not load the expected visitors for this unit."
            onRetry={reload}
          />
        ) : view === 'empty' ? (
          <PassEmpty>
            {canAdd
              ? `Nobody is expected for ${hostFirst} yet. Add a visitor so the front desk knows to let them in.`
              : `Nobody is expected for ${hostFirst}.`}
          </PassEmpty>
        ) : (
          <Staleable stale={passes.refreshing}>
            <PassList>
              {rows.map((pass, index) => {
                const name = visitorName(pass);
                const paused = pass.state === 'paused';

                return (
                  <PassRow key={pass.pass_id} {...listItemMotion(index)} layout={false}>
                    <PassLead>
                      <PassName>
                        {name}
                        {pass.company ? <PassCompany> · {pass.company}</PassCompany> : null}
                      </PassName>
                      <PassMeta>{describeSchedule(pass, { today: data?.today })}</PassMeta>
                      <PassMeta>{nextLine(pass, data?.zone ?? '', data?.today ?? '')}</PassMeta>
                    </PassLead>
                    <PassSide>
                      <StatusPill tone={toneOf(pass)} dot>
                        {stateLabel(pass)}
                      </StatusPill>
                      {mayEdit(pass) || mayEnd(pass) ? (
                        <PassActions>
                          {mayEdit(pass) ? (
                            <AppButton
                              variant="secondary"
                              size="compact"
                              aria-label={`Edit ${possessive(name)} pass`}
                              onClick={() => openEditor(pass)}
                            >
                              Edit
                            </AppButton>
                          ) : null}
                          {!mayEdit(pass) ? null : paused ? (
                            <AppButton
                              variant="secondary"
                              size="compact"
                              aria-label={`Resume ${possessive(name)} pass`}
                              disabled={resuming === pass.pass_id}
                              onClick={() => resume(pass)}
                            >
                              <StatusLabel
                                state={resuming === pass.pass_id ? 'busy' : 'idle'}
                                labels={{ idle: 'Resume', busy: 'Resuming…' }}
                              />
                            </AppButton>
                          ) : (
                            <AppButton
                              variant="secondary"
                              size="compact"
                              aria-label={`Pause ${possessive(name)} pass`}
                              onClick={() => setConfirming({ kind: 'pause', pass })}
                            >
                              Pause
                            </AppButton>
                          )}
                          {mayEnd(pass) ? (
                            <AppButton
                              variant="ghost"
                              size="compact"
                              aria-label={`End ${possessive(name)} pass`}
                              onClick={() => setConfirming({ kind: 'end', pass })}
                            >
                              End
                            </AppButton>
                          ) : null}
                        </PassActions>
                      ) : null}
                    </PassSide>
                  </PassRow>
                );
              })}
            </PassList>
          </Staleable>
        )}
      </StateSwitch>

      {(canAdd || rows.some(mayEdit)) && data ? (
        <PassEditorModal
          key={editorKey}
          open={editor !== null}
          onClose={() => setEditor(null)}
          mode={editor?.pass ? 'staff' : editorMode}
          host={{ occupancy_id: occupant.id, name: hostName, unit_label: unitLabel }}
          limits={data.limits}
          today={data.today}
          zone={data.zone}
          initial={editor?.pass ?? undefined}
          notice="The household will be told."
          onSubmit={spec =>
            editor?.pass
              ? updatePass(editor.pass.pass_id, spec)
              : createPass(buildingId, occupant.id, spec)
          }
          onSaved={() => {
            setEditor(null);
            reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirming?.kind === 'pause'}
        title={`Pause ${possessive(confirmFirst)} pass?`}
        message={`The front desk won't see ${confirmFirst} on the expected list until the pass is resumed.`}
        primaryLabel="Pause pass"
        dismissLabel="Never mind"
        onPrimary={async () => {
          if (confirming) await runAction(confirming.pass, 'pause');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />

      {/* The safe option — "Keep pass" — takes the focus: ConfirmDialog puts
          data-autofocus on its dismiss, so a double Enter keeps the pass. */}
      <ConfirmDialog
        open={confirming?.kind === 'end'}
        title={`End ${possessive(confirmFirst)} pass?`}
        message={`The front desk will stop expecting ${confirmFirst}. An ended pass can't be restarted; add a new one if they need it again.`}
        primaryLabel="End pass"
        primaryVariant="destructive"
        dismissLabel="Keep pass"
        onPrimary={async () => {
          if (confirming) await runAction(confirming.pass, 'revoke');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
    </PassSection>
  );
};

/* ──────────────────────────────  Styles  ──────────────────────────────── */

/* One resident's row and their expected visitors, as one block. */
export const OccupantBlock = styled.div`
  min-width: 0;

  & + & {
    margin-top: ${spacing.sm};
  }
`;

/* Hangs under the resident's row, threaded to it by a hairline on the left. */
const PassSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  margin-top: ${spacing.xs};
  padding-left: ${spacing.sm};
  border-left: 2px solid ${palette.line};

  ${down('sm')} {
    padding-left: ${spacing.xs};
  }
`;

const PassHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
`;

const PassHeading = styled.h4`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};

  /* Focused from code only (after a pass is ended), never tabbed to. */
  &:focus {
    outline: none;
  }
`;

const PassEmpty = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const PassList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* THE TEXT KEEPS A FLOOR. A row that holds actions wraps instead of
   squeezing: the visitor's name and schedule take at least PASS_LEAD_MIN and
   grow, and the pill and the actions drop to a line of their own when the two
   cannot share one (the parcel shelf row rule, parcel-desk.layout.test.ts). */
const PASS_LEAD_MIN = space(20);

const PassRow = m.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

const PassLead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  flex: 1 1 ${PASS_LEAD_MIN};
  min-width: 0;
`;

const PassSide = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  flex: 0 1 auto;
  min-width: 0;
`;

const PassActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  flex: 0 1 auto;
  min-width: 0;
`;

const PassName = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const PassCompany = styled.span`
  font-weight: ${typography.weight.regular};
  color: ${palette.slate};
`;

const PassMeta = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;
